import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Drawer,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PaymentIcon from '@mui/icons-material/Payment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useNavigate } from 'react-router-dom';
import { useOverridePath } from '../contexts/AppContext';
import { callCloudRun } from '../lib/cloudRun';

interface HelpCenterProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  { label: 'Billing & Payments', icon: <PaymentIcon /> },
];

const PAYMENT_OPTIONS = [
  {
    label: 'Self Serve → Coach Led Program Upgrade',
    description: 'Upgrade from a self-serve plan to a coach-led program.',
    icon: <DirectionsRunIcon />,
    url: 'https://www.rhwb.org/_paylink/AZ-ETwwu',
  },
  {
    label: 'Merchandise & Gear',
    description: 'Shop RHWB branded gear and equipment.',
    icon: <StorefrontIcon />,
    url: '', // TODO: add Wix merchandise payment link when ready
  },
];

const HelpCenter: React.FC<HelpCenterProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const withOverride = useOverridePath();
  const [view, setView] = useState<'home' | 'form' | 'payment'>('home');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleClose = () => {
    onClose();
    // reset state after drawer animation
    setTimeout(() => {
      setView('home');
      setSelectedCategory('');
      setMessage('');
    }, 300);
  };

  const handleCategorySelect = (label: string) => {
    if (label === 'Billing & Payments') {
      setView('payment');
      return;
    }
    setSelectedCategory(label);
    setView('form');
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await callCloudRun('/api/support/submit', { category: selectedCategory, message });
      setSnackbarOpen(true);
      handleClose();
    } catch (err) {
      console.error('[HELP CENTER] Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            maxHeight: '80vh',
            px: 2,
            pb: 4,
            overflowY: 'auto',
          },
        }}
      >
        {view === 'payment' ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2, pb: 2 }}>
              <IconButton onClick={() => setView('home')} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Make a Payment
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              {PAYMENT_OPTIONS.filter(o => o.url).map(({ label, description, icon, url }) => (
                <Card key={label} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ color: 'success.main' }}>{icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{label}</Typography>
                      <Typography variant="body2" color="text.secondary">{description}</Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                      sx={{
                        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #1b5e20 0%, #145214 100%)' },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Pay Now
                    </Button>
                  </Box>
                </Card>
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              You'll be redirected to our secure Wix checkout page.
            </Typography>
          </Box>
        ) : view === 'home' ? (
          <Box>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 2,
                pb: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Help & Support
              </Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Chat with Veer */}
            <Card
              variant="outlined"
              sx={{
                mb: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                borderColor: '#667eea40',
              }}
            >
              <CardActionArea
                onClick={() => {
                  handleClose();
                  navigate(withOverride('/veer'));
                }}
                sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}
              >
                <Avatar
                  src="/veer-ai-transparent-small.png"
                  sx={{
                    width: 40,
                    height: 40,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Chat with Veer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get instant answers from your AI coach
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>

            {/* Category tiles */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
              }}
            >
              {CATEGORIES.map(({ label, icon }) => (
                <Card
                  key={label}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <CardActionArea
                    onClick={() => handleCategorySelect(label)}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.5,
                      minHeight: 80,
                    }}
                  >
                    <Box sx={{ color: 'primary.main' }}>{icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {label}
                    </Typography>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Box>
        ) : (
          <Box>
            {/* Form header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pt: 2,
                pb: 2,
              }}
            >
              <IconButton onClick={() => setView('home')} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedCategory}
              </Typography>
            </Box>

            <Chip
              label={selectedCategory}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
            />

            <TextField
              multiline
              minRows={3}
              fullWidth
              label="Describe your request"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please provide as much detail as possible..."
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              fullWidth
              disabled={!message.trim() || submitting}
              onClick={handleSubmit}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #1877F2 0%, #0E5FD3 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0E5FD3 0%, #0A4EB0 100%)',
                },
              }}
            >
              {submitting ? 'Submitting…' : 'Submit Request'}
            </Button>
          </Box>
        )}
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Your request has been received! We'll follow up via email within 24–48 hours.
        </Alert>
      </Snackbar>
    </>
  );
};

export default HelpCenter;
